<script setup lang="ts">
import { computed, onMounted, watch } from "vue";

import { appClosed } from "@/analytics/events/other";
import { trackEvent, trackUserSettingsChanged } from "@/analytics/track";
import { handleDragPointerMove, handleDragPointerUp } from "@/components/gestures/drag";
import {
  handlePinchPointerDown,
  handlePinchPointerMove,
  handlePinchPointerUp,
} from "@/components/gestures/pinch";
import { initWheelHandler } from "@/components/gestures/wheel";
import GlobalError from "@/components/modal/GlobalError.vue";
import { useBoardStore } from "@/store/board";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useModalStore } from "@/store/modal";
import { initPointerHandler } from "@/utils/dom/pointer";

import { useEffectiveInputSafety } from "../modal/settings/useEffectiveInputSafety";
import AppLoadingIndicator from "./AppLoadingIndicator.vue";
import AppToaster from "./internal/AppToaster/AppToaster.vue";
import NetStatistics from "./internal/NetStatistics/NetStatistics.vue";

const modal = computed(() => useModalStore().modal);

onMounted(() => {
  initWheelHandler();
  initPointerHandler({
    down: (id, pos) => handlePinchPointerDown(id, pos),
    move: (id, pos, move) => {
      handlePinchPointerMove(id);
      handleDragPointerMove(id, pos, move);
    },
    up: (id, pos, target) => {
      handlePinchPointerUp(id);
      handleDragPointerUp(id, pos, target);
    },
  });
  useEffectiveInputSafety();
  watch(
    () => useClientSettingsStore().stickyFont,
    (font, old) => stickyFontCss(font, !!old),
    { immediate: true },
  );
  watch(
    () => useBoardStore().board && { ...useClientSettingsStore() }, // Shallow copy so we can identify changes
    (newSettings, oldSettings) => {
      if (newSettings && oldSettings) {
        trackUserSettingsChanged(newSettings, oldSettings);
      }
    },
  );
  // Track when the user closes the app or navigates away
  const trackClose = () => trackEvent(appClosed());
  window.addEventListener("beforeunload", trackClose, { once: true });
});

function stickyFontCss(fontFamily: string, replace: boolean) {
  const css = document.styleSheets[0];
  if (replace) {
    css.deleteRule(0);
  }
  css.insertRule(`:root { --card-font: ${fontFamily} }`, 0);
}
</script>

<template>
  <div>
    <RouterView />
    <NetStatistics />
    <GlobalError />
    <AppLoadingIndicator />
    <AppToaster />
    <!-- @vue-ignore -->
    <component :is="modal.component" v-if="modal" v-bind="modal.attrs" />
  </div>
</template>

<style lang="scss">
@use "@/styles/font-families";
@use "@/styles/global";
@use "@/styles/tooltip";
</style>
